// Score management module for PPT Hero game
export default class ScoreManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Update score display
  updateScore(newScore = null) {
    try {
      if (newScore !== null) {
        this.scene.score = newScore;
      }
      
      if (this.scene.scoreText) {
        this.scene.scoreText.setText(`Score: ${this.scene.score || 0}`);
      }
      
      // Update star rating if available
      if (this.scene.stars && Array.isArray(this.scene.stars)) {
        this.scene.updateStarRating(this.scene.stars, this.scene.score || 0);
      }
      
    } catch (error) {
      console.error('Error updating score:', error);
    }
  }

  // Add points to score
  addPoints(points) {
    try {
      this.scene.score = (this.scene.score || 0) + points;
      this.updateScore();
      
      // Show points notification
      if (points > 0) {
        this.scene.showCenterNotification(`+${points} points!`, 'success', 1500);
      }
      
    } catch (error) {
      console.error('Error adding points:', error);
    }
  }

  // Subtract points from score
  subtractPoints(points) {
    try {
      this.scene.score = Math.max(0, (this.scene.score || 0) - points);
      this.updateScore();
      
      // Show penalty notification
      if (points > 0) {
        this.scene.showCenterNotification(`-${points} points`, 'warning', 1500);
      }
      
    } catch (error) {
      console.error('Error subtracting points:', error);
    }
  }

  // Calculate level score
  calculateLevelScore(levelData) {
    try {
      let baseScore = 100;
      
      // Time bonus
      if (this.scene.timeRemaining > 0) {
        baseScore += Math.floor(this.scene.timeRemaining * 2);
      }
      
      // Shortcut usage bonus
      if (this.scene.analyticsData && this.scene.analyticsData.shortcutsUsed) {
        const shortcutsUsed = Object.keys(this.scene.analyticsData.shortcutsUsed).length;
        baseScore += shortcutsUsed * 10;
      }
      
      // Error penalty
      if (this.scene.analyticsData && this.scene.analyticsData.errors) {
        const totalErrors = this.scene.analyticsData.errors.misstypedShortcuts + 
                           this.scene.analyticsData.errors.incorrectSequences + 
                           this.scene.analyticsData.errors.missclicks;
        baseScore -= totalErrors * 5;
      }
      
      // Efficiency bonus
      const efficiency = this.scene.calculateEfficiency();
      baseScore += Math.floor(efficiency / 10);
      
      return Math.max(50, baseScore);
      
    } catch (error) {
      console.error('Error calculating level score:', error);
      return 100;
    }
  }

  // Get high score
  getHighScore() {
    try {
      const savedScore = localStorage.getItem('pptHeroHighScore');
      return savedScore ? parseInt(savedScore) : 0;
    } catch (error) {
      console.error('Error getting high score:', error);
      return 0;
    }
  }

  // Save high score
  saveHighScore() {
    try {
      const currentScore = this.scene.score || 0;
      const highScore = this.getHighScore();
      
      if (currentScore > highScore) {
        localStorage.setItem('pptHeroHighScore', currentScore.toString());
        this.scene.showCenterNotification('🏆 New High Score!', 'success', 3000);
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Error saving high score:', error);
      return false;
    }
  }

  // Reset score
  resetScore() {
    try {
      this.scene.score = 0;
      this.updateScore();
      this.scene.showCenterNotification('Score reset', 'info', 1500);
      
    } catch (error) {
      console.error('Error resetting score:', error);
    }
  }

  // Get score percentage
  getScorePercentage() {
    try {
      const maxPossibleScore = this.scene.levels ? this.scene.levels.length * 150 : 1000;
      return Math.round(((this.scene.score || 0) / maxPossibleScore) * 100);
      
    } catch (error) {
      console.error('Error calculating score percentage:', error);
      return 0;
    }
  }

  // Get score rank
  getScoreRank() {
    try {
      const percentage = this.getScorePercentage();
      
      if (percentage >= 90) return 'S';
      if (percentage >= 80) return 'A';
      if (percentage >= 70) return 'B';
      if (percentage >= 60) return 'C';
      if (percentage >= 50) return 'D';
      return 'F';
      
    } catch (error) {
      console.error('Error calculating score rank:', error);
      return 'F';
    }
  }

  // Show score summary
  showScoreSummary() {
    try {
      const score = this.scene.score || 0;
      const rank = this.getScoreRank();
      const percentage = this.getScorePercentage();
      
      const summary = `Final Score: ${score}\nRank: ${rank}\nPercentage: ${percentage}%`;
      
      this.scene.showCenterNotification(summary, 'success', 4000);
      
    } catch (error) {
      console.error('Error showing score summary:', error);
    }
  }

  // Export score data
  exportScoreData() {
    try {
      return {
        currentScore: this.scene.score || 0,
        highScore: this.getHighScore(),
        rank: this.getScoreRank(),
        percentage: this.getScorePercentage(),
        exportDate: new Date().toISOString()
      };
      
    } catch (error) {
      console.error('Error exporting score data:', error);
      return null;
    }
  }

  // Import score data
  importScoreData(data) {
    try {
      if (data.currentScore !== undefined) {
        this.scene.score = data.currentScore;
        this.updateScore();
        return true;
      }
      
      return false;
      
    } catch (error) {
      console.error('Error importing score data:', error);
      return false;
    }
  }
}



